import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, FileType2, Mail, Check, Circle, Loader2, ShieldCheck, Users,
  Gavel, ListChecks, AlertTriangle, HelpCircle, X, Plus, Send, Repeat,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

function download(res, fallback) {
  const blob = new Blob([res.data], { type: res.headers["content-type"] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const m = /filename="?([^"]+)"?/i.exec(res.headers["content-disposition"] || "");
  a.download = m ? m[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const COUNT_ICONS = {
  attendees: Users, decisions: Gavel, actions: ListChecks,
  risks: AlertTriangle, questions: HelpCircle, transcript_segments: FileText,
};
const COUNT_LABELS = {
  attendees: "Attendees", decisions: "Decisions", actions: "Action items",
  risks: "Risks", questions: "Open questions", transcript_segments: "Transcript segments",
};

export const MomExport = ({ meetingId, meetingTitle }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [includeDocx, setIncludeDocx] = useState(true);
  const [note, setNote] = useState("");
  const [circulation, setCirculation] = useState("organizer");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/meetings/${meetingId}/mom/preview`)
      .then(({ data }) => {
        setInfo(data);
        setRecipients((data.recipients || []).map((r) => r.email));
        if (data.circulation) setCirculation(data.circulation);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [meetingId]);

  useEffect(load, [load]);

  async function setMode(mode) {
    const prev = circulation;
    setCirculation(mode);
    try {
      await api.patch(`/meetings/${meetingId}/mom/settings`, { circulation: mode });
      toast.success(
        mode === "off" ? "Automatic circulation turned off"
          : mode === "all" ? "Minutes will go to all attendees automatically"
            : "Minutes will come to you first for review"
      );
    } catch (e) {
      setCirculation(prev);
      toast.error(formatApiError(e));
    }
  }

  async function grab(kind) {
    setBusy(kind);
    try {
      const res = await api.get(`/meetings/${meetingId}/mom.${kind}`, { responseType: "blob" });
      download(res, `${meetingTitle || "minutes"}_MoM.${kind}`);
      toast.success(kind === "pdf" ? "Minutes PDF downloaded" : "Word document downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  async function sendMail() {
    setBusy("email");
    try {
      const { data } = await api.post(`/meetings/${meetingId}/mom/email`, {
        recipients,
        include_docx: includeDocx,
        note: note.trim() || null,
      });
      toast.success(`Minutes sent to ${data.recipients.length} recipient(s)`);
      setDialog(false);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  function addEmail() {
    const v = newEmail.trim().toLowerCase();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!recipients.includes(v)) setRecipients([...recipients, v]);
    setNewEmail("");
  }

  if (loading && !info) {
    return (
      <div className="space-y-4" data-testid="mom-loading">
        <Skeleton className="h-32 rounded-3xl" />
        <Skeleton className="h-72 rounded-3xl" />
      </div>
    );
  }

  const sections = info?.sections || [];
  const populated = sections.filter((s) => s.populated).length;

  return (
    <div data-testid="mom-export" className="space-y-6">
      {/* ——— header / actions ——— */}
      <section className="yl-surface yl-beam p-6 lg:p-7">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0">
            <div className="eyebrow">Minutes of meeting · comprehensive document</div>
            <h2 className="font-heading text-2xl sm:text-3xl mt-3 leading-tight">
              18-section branded minutes
            </h2>
            <p className="text-sm text-muted-foreground mt-2.5 max-w-xl leading-relaxed">
              Attendance, agenda coverage, the discussion record, every decision with its approval trail, owned
              action items, risks, open questions, meeting health and the transcript quality statement — as a
              print-ready PDF or an editable Word file.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <span data-testid="mom-doc-id">{info?.doc?.id}</span>
              <span>Version {info?.doc?.version}</span>
              <span>{info?.doc?.classification}</span>
            </div>
            {info?.last_sent_at && (
              <div
                data-testid="mom-last-sent"
                className="mt-4 inline-flex items-center gap-2 rounded-full tone-sage px-3.5 py-1.5 text-[11px] font-semibold"
              >
                <Check className="w-3 h-3" strokeWidth={3} />
                Last circulated {new Date(info.last_sent_at).toLocaleString()} to{" "}
                {(info.last_sent_to || []).length} recipient(s)
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2.5 w-full lg:w-auto lg:shrink-0">
            <button
              type="button"
              data-testid="mom-download-pdf"
              onClick={() => grab("pdf")}
              disabled={!!busy}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-lift hover:bg-[hsl(var(--primary-hover))] transition-colors disabled:opacity-60"
            >
              {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" strokeWidth={2.2} />}
              Download PDF
            </button>
            <button
              type="button"
              data-testid="mom-download-docx"
              onClick={() => grab("docx")}
              disabled={!!busy}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-card text-sm font-bold hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-60"
            >
              {busy === "docx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileType2 className="w-4 h-4" strokeWidth={2.2} />}
              Word (.docx)
            </button>
            <button
              type="button"
              data-testid="mom-email-btn"
              onClick={() => setDialog(true)}
              disabled={!!busy}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-card text-sm font-bold hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-60"
            >
              <Mail className="w-4 h-4" strokeWidth={2.2} />
              Email attendees
            </button>
          </div>
        </div>

        {info?.email_enabled === false && (
          <div className="mt-5 flex items-start gap-2.5 rounded-2xl tone-apricot p-4" data-testid="mom-email-disabled">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-xs leading-relaxed">
              Email is not configured on this deployment, so the minutes cannot be circulated automatically.
              Downloads work normally.
            </p>
          </div>
        )}
      </section>

      {/* ——— auto circulation ——— */}
      <section className="yl-surface p-6 lg:p-7" data-testid="mom-circulation">
        <h3 className="font-heading text-lg sm:text-xl flex items-center gap-2.5">
          <Repeat className="w-4 h-4 text-primary" strokeWidth={2} />
          Automatic circulation
        </h3>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-xl leading-relaxed">
          What should happen the moment AI processing finishes? Nobody has to remember to send anything.
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {[
            { k: "organizer", t: "Review first", d: "Minutes are emailed to you alone. You forward them when you are happy." },
            { k: "all", t: "Send to everyone", d: "Minutes go straight to every attendee as soon as they are ready." },
            { k: "off", t: "Do not send", d: "Nothing is emailed automatically. Download or circulate manually." },
          ].map((o) => (
            <button
              key={o.k}
              type="button"
              data-testid={`mom-circulation-${o.k}`}
              aria-pressed={circulation === o.k}
              onClick={() => setMode(o.k)}
              className={`rounded-2xl border p-4 text-left transition-colors ${
                circulation === o.k ? "border-primary bg-secondary/60" : "border-border hover:border-primary/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold">{o.t}</span>
                {circulation === o.k && (
                  <Check className="w-3.5 h-3.5 shrink-0" style={{ color: "hsl(var(--ink-success))" }} strokeWidth={3} />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{o.d}</p>
            </button>
          ))}
        </div>
      </section>

      {/* ——— counts ——— */}
      <section className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Object.entries(info?.counts || {}).map(([k, v]) => {
          const Icon = COUNT_ICONS[k] || Circle;
          return (
            <div key={k} data-testid={`mom-count-${k}`} className="yl-surface p-5">
              <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.9} />
              <div className="font-heading text-3xl leading-none mt-3">{v}</div>
              <div className="eyebrow text-[9px] mt-2 text-muted-foreground">{COUNT_LABELS[k] || k}</div>
            </div>
          );
        })}
      </section>

      {/* ——— section checklist ——— */}
      <section className="yl-surface p-6 lg:p-7" data-testid="mom-sections">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg sm:text-xl flex items-center gap-2.5">
              <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={2} />
              Document completeness
            </h3>
            <p className="text-xs text-muted-foreground mt-1.5">
              Empty sections still appear in the document, clearly marked as not recorded.
            </p>
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {populated} / {sections.length} sections populated
          </div>
        </div>

        <div className="mt-5 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${sections.length ? (populated / sections.length) * 100 : 0}%`,
              background: "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--navy)))",
              transition: "width .7s cubic-bezier(.2,.7,.2,1)",
            }}
          />
        </div>

        <ul className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1">
          {sections.map((s) => (
            <li
              key={s.no}
              data-testid={`mom-section-${s.no}`}
              className="flex items-center gap-3 py-2.5 border-b border-border/70"
            >
              <span className="font-mono text-[10px] text-muted-foreground w-5 shrink-0">
                {String(s.no).padStart(2, "0")}
              </span>
              <span className={`text-sm flex-1 truncate ${s.populated ? "font-semibold" : "text-muted-foreground"}`}>
                {s.title}
              </span>
              {s.populated ? (
                <Check className="w-3.5 h-3.5 shrink-0" style={{ color: "hsl(var(--ink-success))" }} strokeWidth={3} />
              ) : (
                <Circle className="w-3 h-3 shrink-0 text-muted-foreground/50" strokeWidth={2} />
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ——— email dialog ——— */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="mom-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Circulate the minutes</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Everyone listed receives the branded PDF — and the editable Word file if you leave it switched on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div>
              <div className="eyebrow text-[9px]">Recipients</div>
              <div className="flex flex-wrap gap-2 mt-2.5">
                {recipients.length === 0 && (
                  <span className="text-xs text-muted-foreground">No attendee emails on record — add one below.</span>
                )}
                {recipients.map((r) => (
                  <span
                    key={r}
                    data-testid={`mom-recipient-${r}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold"
                  >
                    {r}
                    <button
                      type="button"
                      aria-label={`Remove ${r}`}
                      data-testid={`mom-recipient-remove-${r}`}
                      onClick={() => setRecipients(recipients.filter((x) => x !== r))}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="w-3 h-3" strokeWidth={2.6} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input
                  data-testid="mom-recipient-input"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                  placeholder="add@email.com"
                  className="h-10 rounded-xl"
                />
                <Button
                  type="button"
                  data-testid="mom-recipient-add"
                  onClick={addEmail}
                  variant="outline"
                  className="h-10 rounded-xl shrink-0"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.4} />
                </Button>
              </div>
            </div>

            <div>
              <div className="eyebrow text-[9px]">Covering note (optional)</div>
              <Textarea
                data-testid="mom-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Anything the attendees should read before opening the document…"
                className="mt-2.5 rounded-xl"
              />
            </div>

            <label className="flex items-center justify-between gap-4 rounded-2xl bg-secondary/50 p-4 cursor-pointer">
              <span className="min-w-0">
                <span className="block text-sm font-bold">Attach the editable Word file</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  The PDF is always attached. Word lets the chair make corrections.
                </span>
              </span>
              <Switch
                data-testid="mom-include-docx"
                checked={includeDocx}
                onCheckedChange={setIncludeDocx}
              />
            </label>

            <Button
              type="button"
              data-testid="mom-email-send"
              onClick={sendMail}
              disabled={busy === "email" || recipients.length === 0}
              className="w-full h-11 rounded-full font-bold"
            >
              {busy === "email"
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating &amp; sending…</>
                : <><Send className="w-4 h-4 mr-2" strokeWidth={2.2} /> Send to {recipients.length} recipient(s)</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MomExport;
